import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound, Upload, Clock, CheckCircle2, XCircle, Ban, X, LogIn, UserPlus } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useCollectorAuth } from "@/context/CollectorAuthContext";
import { track, EVENT } from "@/lib/analytics";
import SEO from "@/components/SEO";
import SocialLoginButtons from "@/components/SocialLoginButtons";
const MAX_BYTES = 10 * 1024 * 1024;
const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp"];

export default function Claim() {
  const { collector, loading } = useCollectorAuth();
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "legacy" ? "legacy" : "code");
  const [claims, setClaims] = useState([]);
  const [reloadKey, setReloadKey] = useState(0);

  // Prefill certificate number if the entry came from /certificates/:n
  const preCert = (params.get("cert") || "").trim().toUpperCase();
  const returnTo = `/claim${preCert ? `?cert=${encodeURIComponent(preCert)}` : ""}`;

  useEffect(() => {
    if (loading || !collector) return;
    // Once per page-open — mirrors the certificate-order-started
    // pattern. No cert / claim code is transmitted.
    track(EVENT.CERTIFICATE_CLAIM_STARTED, {}, { once: true, key: "claim-start" });
    api.get("/me/claims")
      .then((r) => setClaims(r.data.claims || []))
      .catch(() => setClaims([]));
  }, [collector, loading, reloadKey]);

  if (loading) {
    return <div className="p-10 text-center text-[var(--smoke)]">Loading…</div>;
  }

  // -------- UNAUTHENTICATED — friendly auth gateway --------
  if (!collector) return <ClaimAuthGateway returnTo={returnTo} preCert={preCert} />;

  const verified = collector.account_status === "active";

  return (
    <div className="container-gm py-16 max-w-3xl" data-testid="claim-page">
      <SEO title="Claim ownership · Collector Registry" />
      <div className="kicker kicker-red">Collector Registry</div>
      <h1 className="h-display text-4xl mt-3">Claim a certificate</h1>
      <p className="text-[var(--smoke)] mt-3 max-w-2xl">
        Two ways to prove a certificate is yours: enter the one-time claim
        code that shipped with it, or upload a photo of the physical
        certificate for our team to verify.
      </p>

      {!verified && (
        <div className="mt-6 rounded-2xl border-2 border-[var(--panini-red)] bg-[var(--panini-red)]/5 p-4 text-sm font-bold text-[var(--panini-red)]" data-testid="claim-verify-needed">
          Please verify your email before submitting claims.
        </div>
      )}

      <div className="mt-8 rounded-2xl border-2 border-[var(--ink)] bg-white p-2 inline-flex gap-1" data-testid="claim-mode-tabs">
        <button
          data-testid="claim-tab-code"
          onClick={() => setMode("code")}
          className={`px-4 py-2 rounded-xl text-sm font-bold inline-flex items-center gap-2 ${mode === "code" ? "bg-[var(--ink)] text-[var(--panini-yellow)]" : "text-[var(--ink)] hover:bg-[var(--panini-yellow-soft)]"}`}
        >
          <KeyRound size={15} /> Claim code
        </button>
        <button
          data-testid="claim-tab-legacy"
          onClick={() => setMode("legacy")}
          className={`px-4 py-2 rounded-xl text-sm font-bold inline-flex items-center gap-2 ${mode === "legacy" ? "bg-[var(--ink)] text-[var(--panini-yellow)]" : "text-[var(--ink)] hover:bg-[var(--panini-yellow-soft)]"}`}
        >
          <Upload size={15} /> Legacy photo proof
        </button>
      </div>

      <div className="mt-6">
        {mode === "code"
          ? <ClaimCodeForm disabled={!verified} prefillCert={preCert} onDone={() => setReloadKey((k) => k + 1)} />
          : <LegacyProofForm disabled={!verified} prefillCert={preCert} onDone={() => setReloadKey((k) => k + 1)} />}
      </div>

      <div className="mt-14">
        <div className="kicker kicker-black">Your claims</div>
        <h2 className="h-display text-2xl mt-2">History &amp; status</h2>
        <ClaimsList claims={claims} onCancel={() => setReloadKey((k) => k + 1)} />
      </div>
    </div>
  );
}

function ClaimCodeForm({ disabled, onDone, prefillCert }) {
  const [certNumber, setCertNumber] = useState(prefillCert || "");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const { refresh } = useCollectorAuth();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/me/claims/code", {
        cert_number: certNumber.trim(),
        claim_code: code.trim(),
      });
      // Anonymous claim event — cert_number is intentionally omitted
      // (spec §4). Once-guard so a rerender never double-fires.
      track(EVENT.CERTIFICATE_CLAIM_COMPLETED, {
        album_year: r.data?.album_year || undefined,
        cover_type: r.data?.cover_type || undefined,
      }, { once: true, key: `claim-${r.data.cert_number}` });
      toast.success(`Ownership confirmed — ${r.data.cert_number} is now yours.`);

      // Phase A — Universal Collector Onboarding. The backend
      // auto-starts onboarding on successful Path A claims. If this
      // collector was brand-new (or never onboarded), refreshing the
      // context will now report `onboarding.status === 'in_progress'`
      // and the guarded /dashboard/claim/success route will bounce them
      // into /onboarding/welcome via CollectorProtectedRoute. If they
      // already finished onboarding we let them land on the existing
      // celebratory success page unchanged.
      await refresh();
      navigate(`/dashboard/claim/success?cert=${encodeURIComponent(r.data.cert_number)}`, { replace: false });
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="rounded-3xl border-2 border-[var(--ink)] bg-white p-6 md:p-8 shadow-[10px_10px_0_var(--panini-yellow)] space-y-5" data-testid="claim-code-form">
      <p className="text-sm text-[var(--smoke)]">
        Fastest path — the 16-character code printed on the certificate insert.
        Instant, atomic verification.
      </p>
      <label className="block">
        <div className="kicker kicker-black text-[11px] mb-2">Certificate number</div>
        <input
          data-testid="claim-code-cert-number"
          className="gm-field font-mono"
          value={certNumber}
          onChange={(e) => setCertNumber(e.target.value)}
          placeholder="GM-2026-0001"
          required
          disabled={disabled}
        />
      </label>
      <label className="block">
        <div className="kicker kicker-black text-[11px] mb-2">Claim code</div>
        <input
          data-testid="claim-code-value"
          className="gm-field font-mono tracking-widest uppercase"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="XXXX-XXXX-XXXX-XXXX"
          required
          disabled={disabled}
        />
        <p className="text-[11px] text-[var(--smoke)] mt-2">
          16 letters/numbers, grouped in fours. Dashes optional.
        </p>
      </label>
      <button
        type="submit"
        disabled={busy || disabled}
        data-testid="claim-code-submit"
        className="btn-red disabled:opacity-60"
      >
        {busy ? "Verifying…" : "Claim certificate"}
      </button>
    </form>
  );
}

function LegacyProofForm({ disabled, onDone, prefillCert }) {
  const [certNumber, setCertNumber] = useState(prefillCert || "");
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!file) { setPreviewUrl(null); return; }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const pickFile = (f) => {
    if (!f) { setFile(null); return; }
    if (!ALLOWED_TYPES.includes(f.type)) {
      toast.error("Please upload a JPEG, PNG, or WebP image.");
      return;
    }
    if (f.size > MAX_BYTES) {
      toast.error("Image is too large. Maximum 10 MB.");
      return;
    }
    setFile(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Please choose a photo of the certificate.");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("cert_number", certNumber.trim());
      fd.append("file", file);
      if (notes) fd.append("notes", notes);
      const r = await api.post("/me/claims/legacy", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      track(EVENT.CERTIFICATE_CLAIM_COMPLETED, {
        album_year: r.data?.claim?.album_year || undefined,
        cover_type: r.data?.claim?.cover_type || undefined,
      }, { once: true, key: `claim-${r.data?.claim?.cert_number || "legacy"}` });
      toast.success(`Claim submitted — we'll email you once it's reviewed (${r.data.claim.cert_number}).`);
      setCertNumber("");
      setNotes("");
      setFile(null);
      onDone?.();
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="rounded-3xl border-2 border-[var(--ink)] bg-white p-6 md:p-8 shadow-[10px_10px_0_var(--panini-red)] space-y-5" data-testid="claim-legacy-form" encType="multipart/form-data">
      <p className="text-sm text-[var(--smoke)]">
        No code? Upload a clear photo of the physical certificate. Our team
        reviews within a few business days.
      </p>
      <label className="block">
        <div className="kicker kicker-black text-[11px] mb-2">Certificate number</div>
        <input
          data-testid="claim-legacy-cert-number"
          className="gm-field font-mono"
          value={certNumber}
          onChange={(e) => setCertNumber(e.target.value)}
          placeholder="GM-2026-0001"
          required
          disabled={disabled}
        />
      </label>
      <label className="block">
        <div className="kicker kicker-black text-[11px] mb-2">Photo of the certificate (JPEG, PNG, or WebP · max 10 MB)</div>
        <input
          data-testid="claim-legacy-file"
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={(e) => pickFile(e.target.files?.[0] || null)}
          className="block w-full text-sm text-[var(--ink)] file:mr-3 file:py-2 file:px-4 file:rounded-full file:border-2 file:border-[var(--ink)] file:bg-[var(--panini-yellow)] file:font-bold file:cursor-pointer"
          required
          disabled={disabled}
        />
        {previewUrl && (
          <div className="mt-3 rounded-xl overflow-hidden border-2 border-[var(--line)] bg-[var(--ink)]" data-testid="claim-legacy-preview">
            <img src={previewUrl} alt="preview" className="max-h-64 w-full object-contain" />
          </div>
        )}
      </label>
      <label className="block">
        <div className="kicker kicker-black text-[11px] mb-2">Notes for reviewer (optional)</div>
        <textarea
          data-testid="claim-legacy-notes"
          className="gm-field"
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Anything that will help our team verify this is yours."
          disabled={disabled}
          maxLength={2000}
        />
      </label>
      <button
        type="submit"
        disabled={busy || disabled}
        data-testid="claim-legacy-submit"
        className="btn-red disabled:opacity-60"
      >
        {busy ? "Uploading…" : "Submit for review"}
      </button>
      <p className="text-[11px] text-[var(--smoke)]">
        Your photo is stored privately and only visible to you and our
        reviewers. EXIF metadata is stripped automatically.
      </p>
    </form>
  );
}

function ClaimsList({ claims, onCancel }) {
  if (!claims.length) {
    return (
      <div className="mt-4 text-[var(--smoke)] text-sm" data-testid="claim-history-empty">
        No claims yet. Submit one above to get started.
      </div>
    );
  }
  const cancel = async (id) => {
    if (!window.confirm("Cancel this pending claim?")) return;
    try {
      await api.post(`/me/claims/${id}/cancel`);
      toast.success("Claim cancelled.");
      onCancel?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };
  return (
    <div className="mt-4 space-y-3" data-testid="claim-history">
      {claims.map((c) => (
        <div key={c.id} className="rounded-2xl border-2 border-[var(--ink)] bg-white p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-3" data-testid={`claim-row-${c.id}`}>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <StatusBadge status={c.status} />
              <Link
                to={`/certificates/${c.cert_number}`}
                className="font-mono text-[var(--panini-red)] font-bold text-sm hover:underline"
                data-testid={`claim-row-cert-${c.id}`}
              >
                {c.cert_number}
              </Link>
              <span className="text-xs text-[var(--smoke)] uppercase tracking-widest">
                {c.method === "claim_code" ? "Claim code" : "Legacy proof"}
              </span>
            </div>
            {c.review_notes && (
              <div className="mt-2 text-sm text-[var(--ink)] bg-[var(--panini-yellow-soft)] rounded-lg px-3 py-2">
                <span className="font-bold">Reviewer note:</span> {c.review_notes}
              </div>
            )}
            <div className="text-[11px] text-[var(--smoke)] mt-2">
              Submitted {new Date(c.created_at).toLocaleString()}
              {c.reviewed_at ? ` · Reviewed ${new Date(c.reviewed_at).toLocaleString()}` : ""}
            </div>
          </div>
          {c.status === "pending" && (
            <button
              onClick={() => cancel(c.id)}
              data-testid={`claim-cancel-${c.id}`}
              className="text-xs px-3 py-1.5 rounded-full border-2 border-[var(--line)] hover:border-[var(--panini-red)] text-[var(--panini-red)] font-bold inline-flex items-center gap-1"
            >
              <X size={12} /> Cancel
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    approved: { bg: "#dcfce7", fg: "#166534", Icon: CheckCircle2, label: "Approved" },
    pending:  { bg: "#fef3c7", fg: "#92400e", Icon: Clock,        label: "Pending review" },
    rejected: { bg: "#fee2e2", fg: "#991b1b", Icon: XCircle,      label: "Not approved" },
    cancelled:{ bg: "#e5e7eb", fg: "#374151", Icon: Ban,          label: "Cancelled" },
  };
  const cfg = map[status] || map.pending;
  const Icon = cfg.Icon;
  return (
    <span
      className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest"
      style={{ background: cfg.bg, color: cfg.fg }}
      data-testid={`claim-status-${status}`}
    >
      <Icon size={12} /> {cfg.label}
    </span>
  );
}


// ============================================================
// Auth gateway — shown when an unauthenticated visitor lands on /claim.
// Preserves the claim intent (and any prefilled cert number) via return_to.
// ============================================================
function ClaimAuthGateway({ returnTo, preCert }) {
  const encoded = encodeURIComponent(returnTo);
  return (
    <div className="container-gm py-16 md:py-20 max-w-2xl" data-testid="claim-gateway">
      <SEO title="Claim a certificate · Collector Registry" />
      <div className="kicker kicker-red">Ownership</div>
      <h1 className="h-display text-4xl md:text-5xl mt-3 leading-none">
        Claim your certificate.
      </h1>
      <p className="mt-4 text-[var(--smoke)] max-w-lg">
        You&apos;ll need a collector account to attach a certificate to your
        identity. Sign in below — or create one in about two minutes.
      </p>

      {preCert && (
        <div className="mt-6 rounded-2xl border-2 border-[var(--ink)] bg-[var(--panini-yellow-soft)] p-4"
              data-testid="claim-gateway-cert-hint">
          <div className="kicker kicker-black text-[10px]">Certificate</div>
          <div className="mt-1 font-mono font-bold text-[var(--panini-red)]">{preCert}</div>
          <div className="text-xs text-[var(--smoke)] mt-1">
            We&apos;ll bring you straight back here after sign-in.
          </div>
        </div>
      )}

      <div className="mt-8 grid gap-6">
        <SocialLoginButtons mode="login" returnTo={returnTo} />

        <div className="grid sm:grid-cols-2 gap-3">
          <Link
            to={`/login?next=${encoded}`}
            data-testid="claim-gateway-signin"
            className="btn-black w-full justify-center text-sm"
          >
            <LogIn size={16} /> Sign in
          </Link>
          <Link
            to={`/register?next=${encoded}`}
            data-testid="claim-gateway-register"
            className="btn-red w-full justify-center text-sm"
          >
            <UserPlus size={16} /> Create account
          </Link>
        </div>
      </div>

      <div className="mt-14 rounded-3xl border-2 border-dashed border-[var(--line)] p-6 bg-white/60"
            data-testid="claim-gateway-why">
        <div className="kicker kicker-black">Why an account?</div>
        <ul className="mt-3 space-y-2 text-sm text-[var(--ink)]">
          <li>· Cryptographic proof that you own the certificate.</li>
          <li>· A permanent Public Collector ID (GM-C000001) to share.</li>
          <li>· One dashboard for every certificate you own, past &amp; future.</li>
        </ul>
      </div>
    </div>
  );
}
